import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DataProvider } from "@/context/DataContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { FeedbackProvider, useFeedback } from "@/context/FeedbackContext";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import Layout from "@/components/Layout";
import FeedbackLauncher from "@/components/FeedbackLauncher";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import AddStudent from "@/pages/AddStudent";
import StudentDetail from "@/pages/StudentDetail";
import Makeups from "@/pages/Makeups";
import Reports from "@/pages/Reports";
import ManageTeachers from "@/pages/ManageTeachers";
import Setup from "@/pages/Setup";
import Settings from "@/pages/Settings";
import EditStudent from "@/pages/EditStudent";
import AdminFeedback from "@/pages/AdminFeedback";

/**
 * Routes split by setup state:
 *   • If the studio hasn't been set up yet, ONLY the /setup screen is
 *     reachable. Any other path redirects there.
 *   • Once set up, /setup redirects home and the full app is reachable.
 */
function AppRoutes() {
    const { needsSetup } = useAuth();
    const { toggleFeedbackMode } = useFeedback();

    // Global Feedback Mode shortcut: ⌘+Shift+F  /  Ctrl+Shift+F
    useKeyboardShortcuts(
        [
            {
                keys: ["f", "F"],
                meta: true,
                shift: true,
                handler: () => toggleFeedbackMode(),
            },
        ],
        { enabled: !needsSetup },
    );

    if (needsSetup) {
        return (
            <Routes>
                <Route path="/setup" element={<Setup />} />
                <Route path="*" element={<Navigate to="/setup" replace />} />
            </Routes>
        );
    }

    return (
        <Layout>
            <Routes>
                <Route path="/setup" element={<Navigate to="/" replace />} />
                <Route path="/" element={<Dashboard />} />
                <Route path="/students" element={<Students />} />
                <Route path="/students/new" element={<AddStudent />} />
                <Route path="/students/:id" element={<StudentDetail />} />
                <Route path="/students/:id/edit" element={<EditStudent />} />
                <Route path="/makeups" element={<Makeups />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/admin/teachers" element={<ManageTeachers />} />
                <Route path="/admin/settings" element={<Settings />} />
                <Route path="/admin/feedback" element={<AdminFeedback />} />
                <Route path="*" element={<Dashboard />} />
            </Routes>
            <FeedbackLauncher />
        </Layout>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
                <SettingsProvider>
                    <DataProvider>
                        <FeedbackProvider>
                            <BrowserRouter>
                                <AppRoutes />
                                <Toaster
                                    position="top-right"
                                    richColors
                                    closeButton
                                    toastOptions={{
                                        style: {
                                            fontFamily: "Effra, system-ui, sans-serif",
                                            fontWeight: 500,
                                        },
                                    }}
                                />
                            </BrowserRouter>
                        </FeedbackProvider>
                    </DataProvider>
                </SettingsProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
